export interface BulkActionProgressItem {
  id: string;
  name: string;
  status: "pending" | "processing" | "success" | "error";
  error?: string;
  retryCount?: number;
}

export interface BulkActionProgress {
  items: BulkActionProgressItem[];
  totalCount: number;
  completedCount: number;
  errorCount: number;
  isComplete: boolean;
}

export interface FilterBasedBulkActionParams<TFilterCriteria> {
  filterCriteria: TFilterCriteria;
  batchSize?: number;
  delayMs?: number;
  maxConcurrent?: number;
  onProgress?: (progress: BulkActionProgress) => void;
}

export class RateLimitedBulkActionExecutor {
  private activeTasks = new Set<Promise<void>>();
  private maxConcurrent: number;
  private delayMs: number;

  constructor(maxConcurrent = 3, delayMs = 1000) {
    this.maxConcurrent = maxConcurrent;
    this.delayMs = delayMs;
  }

  async executeBulkAction<T>(
    items: T[],
    action: (item: T, index: number) => Promise<void>,
    onProgress?: (completed: number, total: number) => void
  ): Promise<void> {
    const results: Promise<void>[] = [];

    for (let i = 0; i < items.length; i++) {
      while (this.activeTasks.size >= this.maxConcurrent) {
        await Promise.race(this.activeTasks);
      }

      const task = this.executeSingleAction(items[i], action, i, onProgress);
      results.push(task);
      this.activeTasks.add(task);

      void task.finally(() => {
        this.activeTasks.delete(task);
      });

      if (i < items.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, this.delayMs));
      }
    }

    await Promise.allSettled(results);
  }

  private async executeSingleAction<T>(
    item: T,
    action: (item: T, index: number) => Promise<void>,
    index: number,
    onProgress?: (completed: number, total: number) => void
  ): Promise<void> {
    try {
      await action(item, index);
      onProgress?.(index + 1, -1);
    } catch (error) {
      throw error;
    }
  }
}
